import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import { Button } from "../../../../components/Button";
import { Input } from "../../../../components/Input";
import { Textarea } from "../../../../components/Textarea";
import { TestIDs } from "../../../../tests/types/testIds";
import { Rounded } from "../../../../theme/Rounded";
import { Spacing } from "../../../../theme/Spacing";
import { EntityIcons } from "../../../../utils/GlobalIcons";
import { FORMS_CREATE_PARAM } from "../../../form/utils/FormUtils";
import {
  SetupCard,
  SetupCardActions,
  SetupCardDescription,
  SetupCardTitle,
} from "./DashboardSetupCard";
import { DashboardSetupCardLabel } from "./DashboardSetupCardLabel";

const FormSheet = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${Spacing.s};
  padding: ${Spacing.m};
  border-radius: ${Rounded.lg};
  border: 1px solid ${({ theme }) => theme.sub};
  pointer-events: none;
  user-select: none;

  & > button {
    align-self: flex-start;
    margin: 0;
  }
`;

type DashboardSetupFormCardProps = {
  featured?: boolean;
  done?: boolean;
};

export const DashboardSetupFormCard: React.FC<DashboardSetupFormCardProps> = ({
  featured,
  done,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <SetupCard
      $featured={featured}
      $done={done}
      $tall={featured}
      data-testid={TestIDs.dashboard.setup.card("FORM")}
    >
      <DashboardSetupCardLabel featured={featured} done={done} />
      <SetupCardTitle $featured={featured} $done={done}>
        {t("dashboard.setup.form.title")}
      </SetupCardTitle>
      <SetupCardDescription $done={done}>
        {t("dashboard.setup.form.description")}
      </SetupCardDescription>

      <FormSheet aria-hidden inert>
        <Input
          title={t("dashboard.setup.form.preview.name")}
          placeholder={t("dashboard.setup.form.preview.name_placeholder")}
          readOnly
          required
        />
        <Input
          title={t("dashboard.setup.form.preview.phone")}
          placeholder="+1 555 555 0101"
          readOnly
          required
        />
        {featured ? (
          <>
            <Input
              title={t("dashboard.setup.form.preview.email")}
              placeholder="name@company.com"
              readOnly
              required
            />
            <Textarea
              title={t("dashboard.setup.form.preview.message")}
              rows={3}
              readOnly
            />
          </>
        ) : null}
        <Button>{t("dashboard.setup.form.preview.submit")}</Button>
      </FormSheet>

      <SetupCardActions>
        <Button
          icon={EntityIcons.Forms}
          onClick={() => navigate(`/forms?${FORMS_CREATE_PARAM}=1`)}
          dataTestId={TestIDs.dashboard.setup.cta("FORM")}
        >
          {t("dashboard.setup.form.cta")}
        </Button>
      </SetupCardActions>
    </SetupCard>
  );
};
